import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { Play, Search } from 'lucide-react';
import { Button, Card, Input, playOnLudolio, toast } from '@ludolio/shared-ui';
import { Cover, EmptyState, PageHeader } from '@/components/common';
import { RemixDialog } from '@/components/RemixDialog';
import { getErrorMessage, labsApi } from '@/lib/api';
import { platformFamily } from '@/lib/format';
import { useLabs } from '@/lib/labs-context';
import { hasNoLabsUnits } from '@/lib/units';
import type { LabDiscoverItem } from '@/lib/types';

export const Route = createFileRoute('/$orgSlug/discover')({
  component: DiscoverPage,
});

function DiscoverPage() {
  const labs = useLabs();
  const navigate = useNavigate();
  const [items, setItems] = useState<LabDiscoverItem[] | null>(null);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [remixReleaseId, setRemixReleaseId] = useState<string | null>(null);
  const canRemix = !hasNoLabsUnits(labs);

  useEffect(() => {
    labsApi
      .discover(labs.organization.slug)
      .then(setItems)
      .catch((err) => setError(getErrorMessage(err)));
  }, [labs.organization.slug]);

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!items || !term) return items;
    return items.filter((item) =>
      item.title.toLowerCase().includes(term)
      || item.unit?.code?.toLowerCase().includes(term)
      || item.credits.some((credit) => credit.name.toLowerCase().includes(term)));
  }, [items, query]);

  return (
    <>
      <PageHeader
        title="Discover"
        actions={items && items.length > 0 ? (
          <div className="relative w-full sm:w-72">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input aria-label="Search projects" placeholder="Search" className="pl-9" value={query} onChange={(event) => setQuery(event.target.value)} />
          </div>
        ) : undefined}
      />

      {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
      {!items && !error && <p className="text-sm text-muted-foreground">Loading…</p>}
      {items?.length === 0 && <EmptyState title="Nothing has been published at your university yet" />}
      {items && items.length > 0 && filtered?.length === 0 && <EmptyState title="No projects match your search" />}

      {filtered && filtered.length > 0 && (
        <ul className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((item) => (
            <li key={item.projectId}>
              <DiscoverCard item={item} onRemix={canRemix ? () => setRemixReleaseId(item.releaseId) : undefined} />
            </li>
          ))}
        </ul>
      )}

      <RemixDialog
        releaseId={remixReleaseId}
        open={Boolean(remixReleaseId)}
        onOpenChange={(open) => {
          if (!open) setRemixReleaseId(null);
        }}
        onCreated={(project) => {
          setRemixReleaseId(null);
          toast.success('Remix created. Download the original source from Lineage.');
          navigate({
            to: '/$orgSlug/projects/$projectId',
            params: { orgSlug: labs.organization.slug, projectId: project.project.id },
            hash: 'lineage',
          });
        }}
      />
    </>
  );
}

function DiscoverCard({ item, onRemix }: { item: LabDiscoverItem; onRemix?: () => void }) {
  const platforms = [...new Set(item.platforms.map(platformFamily))].join(' | ');
  const team = item.credits.map((credit) => credit.name).slice(0, 3).join(', ');
  const parent = item.parent
    ? `Remix of ${item.parent.title}${item.parent.withdrawn ? ' (taken down)' : ''}`
    : '';

  return (
    <Card className="flex h-full flex-col overflow-hidden">
      <Cover src={item.bannerUrl} title={item.title} />
      <div className="flex flex-1 flex-col gap-2 p-4">
        <h2 className="line-clamp-1 font-semibold">{item.title}</h2>
        <p className="line-clamp-1 min-h-5 text-sm text-muted-foreground">
          {[item.unit?.code, team].filter(Boolean).join(' | ') || 'Ludolio Labs'}
        </p>
        <p className="line-clamp-1 min-h-5 text-xs text-muted-foreground">{parent}</p>
        <p className="line-clamp-2 min-h-10 text-sm">{item.description ?? ''}</p>
        {item.contentNotes && <p className="line-clamp-1 text-xs text-muted-foreground">Content: {item.contentNotes}</p>}
        <div className="mt-auto flex items-center justify-between gap-3 pt-3">
          <span className="text-xs text-muted-foreground">{platforms}</span>
          <div className="flex shrink-0 items-center justify-end gap-2">
            {item.remixable && onRemix && (
              <Button size="sm" variant="outline" onClick={onRemix}>Remix</Button>
            )}
            <Button size="sm" onClick={() => playOnLudolio(item.appId)}>
              <Play />
              Play
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}
